import { groceryLookupKey } from './ingredientQuantity.js'
import { mergeRecipeSourceOntoItem, stampSingleRecipeSource } from './recipeShoppingListSources.js'

export function mergeUniqueGroceryItems(existingItems, incomingItems) {
  const existing = Array.isArray(existingItems) ? existingItems : []
  const incoming = Array.isArray(incomingItems) ? incomingItems : []
  const keyFor = (it) => {
    if (!it || typeof it !== 'object') return ''
    // Prefer product name so the live shop list never accumulates same-name duplicates
    // from different source ids (meal plan, recipe, chat, cart hydrate).
    const name = (it.item || it.name || '').toString().trim().toLowerCase()
    if (name) return `name:${name}`
    if (it.id != null && String(it.id).trim() !== '') return `id:${String(it.id)}`
    return ''
  }

  const out = existing.slice()
  const indexByKey = new Map()
  out.forEach((it, index) => {
    const key = keyFor(it)
    if (key && !indexByKey.has(key)) indexByKey.set(key, index)
  })
  let didChange = false
  for (const it of incoming) {
    const key = keyFor(it)
    if (!key) continue
    if (indexByKey.has(key)) {
      const index = indexByKey.get(key)
      const merged = mergeRecipeSourceOntoItem(out[index], it)
      if (merged !== out[index]) {
        out[index] = merged
        didChange = true
      }
      continue
    }
    indexByKey.set(key, out.length)
    out.push(it)
    didChange = true
  }
  return didChange ? out : existing
}

/**
 * When adding from a recipe list, stamp each line with recipe provenance so
 * Shop can group “By recipe”. Does not overwrite fields already present.
 */
export function stampRecipeSourceOnItems(items, { isRecipe = false, listId = null, listName = null } = {}) {
  const list = Array.isArray(items) ? items : []
  if (!isRecipe) return list
  const recipeName = listName != null ? String(listName).trim() : ''
  if (!recipeName) return list
  const recipeId = listId != null && String(listId).trim() !== '' ? String(listId) : null
  return list.map((it) => stampSingleRecipeSource(it, { recipeId, recipeName }))
}

export function buildShoppingListSignature(items) {
  const list = Array.isArray(items) ? items : []
  return JSON.stringify(
    list.map((it) => ({
      id: it?.id ?? null,
      item: (it?.item || '').toString().trim().toLowerCase(),
      completed: !!it?.completed,
      category: it?.category ?? null,
      aisle_id: it?.aisle_id ?? null,
      aisle_name: it?.aisle_name ?? null,
      source_recipe_id: it?.source_recipe_id ?? null,
      source_recipe_name: it?.source_recipe_name ?? null,
      source_recipe_ids: Array.isArray(it?.source_recipe_ids) ? it.source_recipe_ids : null,
      source_recipe_names: Array.isArray(it?.source_recipe_names) ? it.source_recipe_names : null,
    }))
  )
}

/**
 * Pure reducer for shopping list updates. Used by UI state orchestration and tests.
 */
export function applyShoppingListUpdateReducer(current, update) {
  const state = current || {}
  const {
    items = [],
    listId = null,
    listName = null,
    isRecipe = false,
    mode = 'replace',
    preserveCurrentOnSameList = false,
    refreshedItems = [],
    loadedListSignature = '',
  } = update || {}

  const mapped = stampRecipeSourceOnItems(Array.isArray(items) ? items : [], {
    isRecipe,
    listId,
    listName,
  })
  const listSignature = `${listId ?? 'none'}:${buildShoppingListSignature(mapped)}`

  if (mode === 'clear') {
    const didChange =
      (state.groceryListItems || []).length > 0 ||
      !!state.loadedSavedListId ||
      !!state.loadedSavedListName ||
      !!state.loadedSavedListIsRecipe ||
      !!state.loadedSavedListOriginalItemNames ||
      !!state.loadedListSignature
    if (!didChange) return { didChange: false, next: state }

    const next = {
      ...state,
      groceryListItems: [],
      loadedSavedListId: null,
      loadedSavedListName: null,
      loadedSavedListIsRecipe: false,
      loadedSavedListOriginalItemNames: null,
      loadedListSignature: '',
    }
    return { didChange, next }
  }

  if (mode === 'refresh_metadata') {
    const currentItems = Array.isArray(state.groceryListItems) ? state.groceryListItems : []
    const incoming = Array.isArray(refreshedItems) ? refreshedItems : []
    if (currentItems.length === 0 || incoming.length === 0) {
      return { didChange: false, next: state }
    }

    const byName = new Map()
    const indexMeta = (rawName, meta) => {
      const exact = (rawName || '').toString().trim().toLowerCase()
      if (!exact) return
      if (!byName.has(exact)) byName.set(exact, meta)
      const stripped = groceryLookupKey(exact)
      if (stripped && !byName.has(stripped)) byName.set(stripped, meta)
    }

    incoming.forEach((it) => {
      const meta = {
        category: it?.category,
        aisle_id: it?.aisle_id,
        aisle_name: it?.aisle_name,
        confidence: it?.confidence,
      }
      indexMeta(it?.item || it?.name, meta)
    })

    let changed = false
    const nextItems = currentItems.map((it) => {
      const exact = (it?.item || it?.name || '').toString().trim().toLowerCase()
      const stripped = groceryLookupKey(exact)
      const match = (exact && byName.get(exact)) || (stripped && byName.get(stripped)) || null
      if (!match) return it

      const merged = {
        ...it,
        category: match.category ?? it.category,
        aisle_id:
          match.aisle_id != null && match.aisle_id !== 'Unknown' && match.aisle_id !== 'Error'
            ? match.aisle_id
            : it.aisle_id,
        aisle_name: match.aisle_name != null && match.aisle_name !== 'Not Found' ? match.aisle_name : it.aisle_name,
        confidence: match.confidence !== undefined ? match.confidence : it.confidence,
      }

      if (
        merged.category === it.category &&
        merged.aisle_id === it.aisle_id &&
        merged.aisle_name === it.aisle_name &&
        merged.confidence === it.confidence
      ) {
        return it
      }
      changed = true
      return merged
    })

    if (!changed) return { didChange: false, next: state }
    return {
      didChange: true,
      next: {
        ...state,
        groceryListItems: nextItems,
      },
    }
  }

  if (mode === 'append') {
    const nextItems = mergeUniqueGroceryItems(state.groceryListItems || [], mapped)
    if (nextItems === (state.groceryListItems || [])) {
      return { didChange: false, next: state }
    }
    return {
      didChange: true,
      next: {
        groceryListItems: nextItems,
        loadedSavedListId: listId ?? null,
        loadedSavedListName: listName,
        loadedSavedListIsRecipe: Boolean(isRecipe),
        loadedSavedListOriginalItemNames:
          isRecipe && mapped.length > 0
            ? mapped.map((i) => (i.item || i.name || '').trim().toLowerCase())
            : null,
        loadedListSignature,
      },
    }
  }

  if (
    String(state.loadedSavedListId || '') === String(listId || '') &&
    state.loadedListSignature === listSignature
  ) {
    return { didChange: false, next: state }
  }

  const nextItems = preserveCurrentOnSameList
    ? mergeUniqueGroceryItems(state.groceryListItems || [], mapped)
    : mapped

  return {
    didChange: true,
    next: {
      groceryListItems: nextItems,
      loadedSavedListId: listId ?? null,
      loadedSavedListName: listName,
      loadedSavedListIsRecipe: false,
      loadedSavedListOriginalItemNames: null,
      loadedListSignature: listSignature,
    },
  }
}

